import type { BeforeSendFn, CaptureResult } from "posthog-js"
import { currentRouteSurface } from "./telemetry"

type ExceptionListItem = {
  type?: unknown
  value?: unknown
  stacktrace?: unknown
}

// posthog-js attaches a `stacktrace.frames` array per exception. The synthetic
// "UnhandledRejection" placeholder has no frames.
function hasStackFrames(exception: ExceptionListItem): boolean {
  const stacktrace = exception?.stacktrace as { frames?: unknown } | undefined
  return Array.isArray(stacktrace?.frames) && stacktrace.frames.length > 0
}

/**
 * PostHog `before_send` handler. Drops third-party / non-actionable exception
 * noise before it is sent to PostHog Error Tracking.
 *
 * IMPORTANT: posthog-js only populates `$exception_list` (an array of
 * `{ type, value }`) on the client at `before_send` time. The flattened
 * `$exception_values` / `$exception_types` arrays seen on stored events are
 * derived server-side during ingestion, so they are `undefined` here.
 * Always match against `$exception_list` — an earlier version read the
 * flattened arrays and therefore silently matched nothing, letting the noise
 * through (found via error-patrol 2026-05-28; original filter PR #1022).
 */
export const filterPosthogEvent: BeforeSendFn = (event) => {
  if (!event) return null
  // Tag every outgoing event with the web runtime platform.
  event.properties = {
    ...event.properties,
    platform: "web",
    route_surface: currentRouteSurface(),
  }

  if (event.event !== "$exception") return event

  const exceptions = (event.properties?.$exception_list ?? []) as ExceptionListItem[]
  const matches = (needle: string) =>
    exceptions.some((e) => {
      const type = typeof e?.type === "string" ? e.type : ""
      const value = typeof e?.value === "string" ? e.value : ""
      return type.includes(needle) || value.includes(needle)
    })

  // Drop AbortError exceptions — fetch aborts from useEffect cleanup or route
  // navigation are noise, not actionable bugs.
  if (matches("AbortError")) return null

  // Meta Pixel fbevents.js calls window.webkit.messageHandlers from the
  // Instagram/Facebook in-app browser bridge; throws in regular iOS Safari
  // where messageHandlers isn't injected. Third-party noise.
  if (matches("window.webkit.messageHandlers")) return null

  // Android equivalent: the Instagram/Facebook in-app browser injects a
  // JS↔native bridge and throws "Java object is gone" when the WebView is torn
  // down mid-call (e.g. "Error invoking postMessage: Java object is gone").
  // Third-party in-app-browser noise we can't fix. (error-patrol 2026-05-29;
  // same class as the webkit filter above.)
  if (matches("Java object is gone")) return null

  // Drop the stackless synthetic "UnhandledRejection" placeholder. posthog-js
  // emits it when a promise rejects with a non-Error reason that carries no
  // message: the exception has type "UnhandledRejection" and no stack frames, so
  // it holds no stack and no reason and is not debuggable as captured. The
  // unhandledrejection listener in the provider records the serialized reason as
  // a separate event when a repeat burst needs diagnosis. (error-tracking
  // hygiene 2026-08-20.)
  // Same class: a stackless synthetic "[Error]" placeholder (window.onerror
  // fired with a non-Error value, seen on Firefox iOS /survey). No message,
  // no frames, nothing to act on. (error-patrol 2026-09-14.)
  if (
    exceptions.length > 0 &&
    exceptions.every((e) => {
      const type = typeof e?.type === "string" ? e.type : ""
      const value = typeof e?.value === "string" ? e.value : ""
      const placeholder = type === "UnhandledRejection" || value === "[Error]"
      return placeholder && !hasStackFrames(e)
    })
  ) {
    return null
  }

  return event
}
